import React, { useState, useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  FiCheck,
  FiDownload,
  FiArrowRight,
  FiClock,
  FiAlertCircle,
  FiFolder,
  FiServer,
  FiMail,
  FiImage,
  FiFileText,
  FiLayout,
  FiBarChart2,
  FiTrendingUp,
  FiSearch,
  FiTarget,
  FiCalendar,
  FiVideo
} from 'react-icons/fi';
import SEO from '../components/SEO';
import { stripeAPI, contactAPI } from '../utils/api';
import './TemplateSuccess.css';

const TemplateSuccess = () => {
  const [searchParams] = useSearchParams();
  const sessionId = searchParams.get('session_id');
  const [sessionData, setSessionData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [assetForm, setAssetForm] = useState({
    name: '',
    email: '',
    propertyName: '',
    assetLink: '',
    notes: ''
  });
  const [formSubmitted, setFormSubmitted] = useState(false);
  const [formLoading, setFormLoading] = useState(false);

  useEffect(() => {
    const fetchSession = async () => {
      if (sessionId) {
        try {
          const { data } = await stripeAPI.getSession(sessionId);
          if (data.success) {
            setSessionData(data);
          } else {
            setError(data.message || 'Payment not completed');
          }
        } catch (err) {
          console.error('Error fetching session:', err);
          setError('Unable to retrieve purchase details. Please contact support.');
        }
      } else {
        setError('No session ID provided');
      }
      setLoading(false);
    };

    fetchSession();
  }, [sessionId]);

  const handleAssetFormChange = (e) => {
    setAssetForm({ ...assetForm, [e.target.name]: e.target.value });
  };

  const handleAssetFormSubmit = async (e) => {
    e.preventDefault();
    setFormLoading(true);

    try {
      await contactAPI.submit({
        name: assetForm.name,
        email: assetForm.email,
        subject: `Asset Submission - ${assetForm.propertyName}`,
        message: `Property Name: ${assetForm.propertyName}\n\nAsset Link: ${assetForm.assetLink}\n\nTemplate: ${sessionData?.template?.title || 'N/A'}\n\nAdditional Notes: ${assetForm.notes || 'None'}`,
        type: 'asset_submission'
      });
      setFormSubmitted(true);
    } catch (err) {
      console.error('Error submitting form:', err);
      alert('Error submitting form. Please try again or email us directly.');
    }
    setFormLoading(false);
  };

  if (loading) {
    return (
      <div className="sfm-loading-screen">
        <div className="sfm-loading-spinner"></div>
        <p>Verifying your purchase...</p>
      </div>
    );
  }

  if (error) {
    return (
      <>
        <SEO title="Purchase Error" />
        <section className="sfm-success section">
          <div className="container">
            <motion.div
              className="sfm-success__content sfm-success__content--error"
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.5 }}
            >
              <div className="sfm-success__icon sfm-success__icon--error">
                <FiAlertCircle />
              </div>
              <h1>Something Went Wrong</h1>
              <p className="sfm-success__message">{error}</p>
              <div className="sfm-success__actions">
                <Link to="/templates" className="btn btn-primary">
                  Back to Templates
                </Link>
                <Link to="/contact" className="btn btn-outline">
                  Contact Support
                </Link>
              </div>
            </motion.div>
          </div>
        </section>
      </>
    );
  }

  const hasFullSetup = sessionData?.addOns?.fullSetup;
  const hasHosting = sessionData?.addOns?.hosting;
  const hasAnalytics = sessionData?.addOns?.analytics;

  return (
    <>
      <SEO title="Purchase Successful" />

      <section className="sfm-success section">
        <div className="container">
          <motion.div
            className="sfm-success__content"
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.5 }}
          >
            <div className="sfm-success__icon">
              <FiCheck />
            </div>

            <h1>Thank You for Your Purchase!</h1>
            <p className="sfm-success__message">
              Your order has been confirmed. {hasFullSetup ? "We'll get started on your setup right away!" : "Your download link is ready below."}
            </p>

            {/* Download Section */}
            {sessionData?.template && (
              <div className="sfm-success__template">
                <h2>Your Template</h2>
                <div className="sfm-success__template-card">
                  <div className="sfm-success__template-info">
                    <h3>{sessionData.template.title}</h3>
                    <p>{sessionData.template.description}</p>

                    {sessionData.downloadUrl && (
                      <>
                        <a
                          href={sessionData.downloadUrl}
                          className="btn btn-primary sfm-success__download-btn"
                          download
                        >
                          <FiDownload />
                          Download Template
                        </a>
                        <div className="sfm-success__expiry">
                          <FiClock />
                          <span>Download link expires in {sessionData.expiresIn}</span>
                        </div>
                      </>
                    )}
                  </div>
                </div>
              </div>
            )}

            {/* Full Setup Section */}
            {hasFullSetup && (
              <motion.div
                className="sfm-success__setup-section"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.3 }}
              >
                <h2><FiFolder /> Submit Your Assets</h2>
                <p className="sfm-success__setup-intro">
                  To get started on your Full Setup, we'll need your branding assets and content.
                  The easiest way is to share a Google Drive, Dropbox, or any cloud storage folder with us.
                </p>

                <div className="sfm-success__asset-checklist">
                  <h3>What to Include:</h3>
                  <ul>
                    <li><FiImage /> <strong>Logo</strong> - High resolution (PNG or SVG preferred)</li>
                    <li><FiImage /> <strong>Property Photos</strong> - Exterior, interior, amenities</li>
                    <li><FiLayout /> <strong>Floor Plans</strong> - Images of available floor plans</li>
                    <li><FiFileText /> <strong>Copy/Text</strong> - Property description, amenity details, contact info</li>
                    <li><FiMail /> <strong>Contact Details</strong> - Address, phone, email, office hours</li>
                  </ul>
                </div>

                <div className="sfm-success__share-instructions">
                  <h3>How to Share:</h3>
                  <ol>
                    <li>Create a folder in Google Drive, Dropbox, or your preferred cloud storage</li>
                    <li>Add all your assets to the folder</li>
                    <li>Share the folder with: <strong><a href="mailto:uploads@silverfoxmedia.co">uploads@silverfoxmedia.co</a></strong></li>
                    <li>Or set sharing to "Anyone with the link can view" and submit the link below</li>
                  </ol>
                </div>

                {!formSubmitted ? (
                  <form className="sfm-success__asset-form" onSubmit={handleAssetFormSubmit}>
                    <h3>Submit Your Asset Link</h3>
                    <div className="sfm-success__form-grid">
                      <div className="sfm-success__form-group">
                        <label htmlFor="name">Your Name *</label>
                        <input
                          type="text"
                          id="name"
                          name="name"
                          value={assetForm.name}
                          onChange={handleAssetFormChange}
                          required
                        />
                      </div>
                      <div className="sfm-success__form-group">
                        <label htmlFor="email">Email *</label>
                        <input
                          type="email"
                          id="email"
                          name="email"
                          value={assetForm.email}
                          onChange={handleAssetFormChange}
                          required
                        />
                      </div>
                      <div className="sfm-success__form-group sfm-success__form-group--full">
                        <label htmlFor="propertyName">Property Name *</label>
                        <input
                          type="text"
                          id="propertyName"
                          name="propertyName"
                          value={assetForm.propertyName}
                          onChange={handleAssetFormChange}
                          placeholder="e.g., Sunrise Apartments"
                          required
                        />
                      </div>
                      <div className="sfm-success__form-group sfm-success__form-group--full">
                        <label htmlFor="assetLink">Cloud Storage Link *</label>
                        <input
                          type="url"
                          id="assetLink"
                          name="assetLink"
                          value={assetForm.assetLink}
                          onChange={handleAssetFormChange}
                          placeholder="https://drive.google.com/... or https://dropbox.com/..."
                          required
                        />
                      </div>
                      <div className="sfm-success__form-group sfm-success__form-group--full">
                        <label htmlFor="notes">Additional Notes (Optional)</label>
                        <textarea
                          id="notes"
                          name="notes"
                          value={assetForm.notes}
                          onChange={handleAssetFormChange}
                          rows="3"
                          placeholder="Any special requests or details about your project..."
                        />
                      </div>
                    </div>
                    <button
                      type="submit"
                      className="btn btn-primary"
                      disabled={formLoading}
                    >
                      {formLoading ? 'Submitting...' : 'Submit Assets'}
                    </button>
                  </form>
                ) : (
                  <div className="sfm-success__form-submitted">
                    <FiCheck />
                    <h3>Assets Submitted!</h3>
                    <p>We've received your submission and will begin working on your site. We'll be in touch within 1-2 business days.</p>
                  </div>
                )}
              </motion.div>
            )}

            {/* Hosting Section */}
            {hasHosting && (
              <motion.div
                className="sfm-success__hosting-section"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.4 }}
              >
                <h2><FiServer /> Your Hosting & Maintenance Plan</h2>
                <div className="sfm-success__hosting-details">
                  <p>Your monthly hosting subscription is now active. Here's what's included:</p>
                  <ul>
                    <li><FiCheck /> <strong>Fast Cloud Hosting</strong> - Your site hosted on high-performance servers</li>
                    <li><FiCheck /> <strong>SSL Certificate</strong> - Secure HTTPS connection included</li>
                    <li><FiCheck /> <strong>Monthly Backups</strong> - Your site is backed up regularly</li>
                    <li><FiCheck /> <strong>Content Updates</strong> - Need changes? Just email us</li>
                    <li><FiCheck /> <strong>Priority Support</strong> - Fast response times for any issues</li>
                  </ul>
                  <div className="sfm-success__hosting-billing">
                    <p><strong>Billing:</strong> $100/month, charged automatically to your card on file.</p>
                    <p>To update your payment method or cancel, email <a href="mailto:information@silverfoxmedia.co">information@silverfoxmedia.co</a></p>
                  </div>
                </div>
              </motion.div>
            )}

            {/* Analytics Setup Section */}
            {hasAnalytics && (
              <motion.div
                className="sfm-success__analytics-section"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.5 }}
              >
                <h2><FiBarChart2 /> Your Analytics Setup</h2>
                <div className="sfm-success__analytics-details">
                  <p>We'll set up comprehensive analytics for your property website. Here's what's included:</p>
                  <ul>
                    <li><FiTrendingUp /> <strong>Google Analytics 4</strong> - Track visitors, page views, and user behavior</li>
                    <li><FiSearch /> <strong>Google Search Console</strong> - Monitor search rankings and keywords</li>
                    <li><FiTarget /> <strong>Conversion Tracking</strong> - Track contact form submissions and phone clicks</li>
                    <li><FiBarChart2 /> <strong>ROI Dashboard</strong> - Custom dashboard to measure marketing performance</li>
                  </ul>
                  <div className="sfm-success__analytics-info">
                    <h3>What We Need From You:</h3>
                    <p>To set up analytics, we'll need access to your Google account or we can create a new one for your property. We'll reach out via email within 1-2 business days to coordinate the setup.</p>
                    <p>Once configured, you'll receive login credentials to your analytics dashboard where you can view real-time traffic, search performance, and ROI metrics.</p>
                  </div>
                </div>
              </motion.div>
            )}

            {/* Book a Call Section - Show for Full Setup or Analytics */}
            {(hasFullSetup || hasAnalytics) && (
              <motion.div
                className="sfm-success__book-call-section"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.6 }}
              >
                <h2><FiCalendar /> Schedule Your Kickoff Call</h2>
                <p>Let's get your project started! Book a quick call to discuss your requirements, timeline, and any questions you have.</p>
                <div className="sfm-success__book-call-benefits">
                  <div className="sfm-success__benefit">
                    <FiVideo />
                    <span>30-minute video call</span>
                  </div>
                  <div className="sfm-success__benefit">
                    <FiCheck />
                    <span>Review your project scope</span>
                  </div>
                  <div className="sfm-success__benefit">
                    <FiClock />
                    <span>Get your timeline</span>
                  </div>
                </div>
                <a
                  href="https://calendly.com/james-silverfoxmedia/30min"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="btn btn-primary sfm-success__book-call-btn"
                >
                  <FiCalendar />
                  Book Your Call
                </a>
                <p className="sfm-success__book-call-note">
                  Can't find a time that works? Email us at <a href="mailto:information@silverfoxmedia.co">information@silverfoxmedia.co</a>
                </p>
              </motion.div>
            )}

            {/* What's Next for Template Only */}
            {!hasFullSetup && !hasHosting && (
              <motion.div
                className="sfm-success__next-steps"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.3 }}
              >
                <h2>What's Next?</h2>
                <ol>
                  <li>Download your template files using the button above</li>
                  <li>Extract the ZIP file to your computer</li>
                  <li>Open the files in your favorite code editor</li>
                  <li>Customize the content, images, and colors to match your brand</li>
                  <li>Upload to your web hosting provider</li>
                </ol>
                <p>Need help? Check out the README.md file included in the download, or <Link to="/contact">contact us</Link> for support.</p>
              </motion.div>
            )}

            <div className="sfm-success__actions">
              <Link to="/templates" className="btn btn-outline">
                Browse More Templates
              </Link>
              {!hasFullSetup && (
                <Link to="/contact" className="btn btn-primary">
                  Need Setup Help?
                  <FiArrowRight />
                </Link>
              )}
            </div>

            <div className="sfm-success__support">
              <p>
                Questions? Our team is here to help.
              </p>
              <p>
                Email <a href="mailto:information@silverfoxmedia.co">information@silverfoxmedia.co</a> or call <a href="tel:9728008105">(972) 800-8105</a>
              </p>
            </div>
          </motion.div>
        </div>
      </section>
    </>
  );
};

export default TemplateSuccess;
